import React, { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/common/ProtectedRoute';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { 
  getSiteContent, saveSiteContent, getEquipment, getProjects, getDistribution 
} from '../../lib/cmsService';
import { INITIAL_PROJECTS, INITIAL_EQUIPMENT, INITIAL_EVENTS, INITIAL_DISTRIBUTION } from '../../data/mockData';
import { INITIAL_SITE_CONTENT } from '../../data/initialCmsData';
import { EquipmentItem, ProjectItem, DistributionTitle, SiteContentConfig } from '../../types';
import {
  Users,
  Film,
  Camera,
  Calendar,
  Globe,
  Newspaper,
  Mail,
  Shield,
  Clock,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Sliders,
  Palette,
  Eye,
  ExternalLink,
  X,
  Save,
  Layers,
  Settings,
  ChevronRight,
} from 'lucide-react';

export const AdminDashboardPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'VIEWER';

  const [siteContent, setSiteContent] = useState<SiteContentConfig>(INITIAL_SITE_CONTENT);
  const [equipmentList, setEquipmentList] = useState<EquipmentItem[]>(INITIAL_EQUIPMENT);
  const [projectList, setProjectList] = useState<ProjectItem[]>(INITIAL_PROJECTS);
  const [distributionList, setDistributionList] = useState<DistributionTitle[]>(INITIAL_DISTRIBUTION);
  const [loading, setLoading] = useState(true);

  // Section 7 (Equipment Showcase) Customizer Modal State
  const [isSection7ModalOpen, setIsSection7ModalOpen] = useState(false);
  const [isSavingSection7, setIsSavingSection7] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  // Editable Form fields for Section 7
  const [s7Eyebrow, setS7Eyebrow] = useState('07 // HARDWARE & OPTICS');
  const [s7Heading, setS7Heading] = useState('CAMERA & DOP RENTAL DIVISION');
  const [s7Description, setS7Description] = useState('Hollywood-grade ARRI Alexa 35, Cooke Anamorphics, RED V-Raptor XL, and professional lighting kits.');
  const [s7CtaLabel, setS7CtaLabel] = useState('BROWSE CATALOGUE');
  const [s7CtaDestination, setS7CtaDestination] = useState('/equipment');
  const [s7BgColor, setS7BgColor] = useState('#F8F9FA');
  const [s7AccentColor, setS7AccentColor] = useState('#B45309');
  const [s7Enabled, setS7Enabled] = useState(true);

  useEffect(() => {
    async function loadDashboardData() {
      try {
        setLoading(true);
        const [content, eq, proj, dist] = await Promise.all([
          getSiteContent(),
          getEquipment(),
          getProjects(),
          getDistribution(),
        ]);
        setSiteContent(content);
        setEquipmentList(eq);
        setProjectList(proj);
        setDistributionList(dist);

        // Initialize Section 7 fields from site content
        const eqSection = content?.homepageSections?.equipment;
        if (eqSection) {
          setS7Eyebrow(eqSection.eyebrow || '07 // HARDWARE & OPTICS');
          setS7Heading(eqSection.heading || 'CAMERA & DOP RENTAL DIVISION');
          setS7Description(eqSection.description || '');
          setS7CtaLabel(eqSection.ctaLabel || 'BROWSE CATALOGUE');
          setS7CtaDestination(eqSection.ctaDestination || '/equipment');
          setS7BgColor(eqSection.backgroundColor || '#F8F9FA');
          setS7AccentColor(eqSection.accentColor || '#B45309');
          setS7Enabled(eqSection.enabled !== false);
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadDashboardData();
  }, []);

  const openSection7Customizer = () => {
    const eqSection = siteContent?.homepageSections?.equipment;
    if (eqSection) {
      setS7Eyebrow(eqSection.eyebrow || '07 // HARDWARE & OPTICS');
      setS7Heading(eqSection.heading || 'CAMERA & DOP RENTAL DIVISION');
      setS7Description(eqSection.description || '');
      setS7CtaLabel(eqSection.ctaLabel || 'BROWSE CATALOGUE');
      setS7CtaDestination(eqSection.ctaDestination || '/equipment');
      setS7BgColor(eqSection.backgroundColor || '#F8F9FA');
      setS7AccentColor(eqSection.accentColor || '#B45309');
      setS7Enabled(eqSection.enabled !== false);
    }
    setIsSection7ModalOpen(true);
  };

  const handleSaveSection7 = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    try {
      setIsSavingSection7(true);
      const currentSections = siteContent.homepageSections || {};
      const updatedEquipmentConfig = {
        id: 'equipment',
        enabled: s7Enabled,
        eyebrow: s7Eyebrow,
        heading: s7Heading,
        description: s7Description,
        ctaLabel: s7CtaLabel,
        ctaDestination: s7CtaDestination,
        backgroundColor: s7BgColor,
        accentColor: s7AccentColor,
        displayOrder: 6,
      };

      const updatedSiteContent: SiteContentConfig = {
        ...siteContent,
        homepageSections: {
          ...currentSections,
          equipment: updatedEquipmentConfig,
        },
      };

      await saveSiteContent(updatedSiteContent);
      setSiteContent(updatedSiteContent);
      setSaveSuccessMsg('Section 7 styling and configuration successfully saved and updated on homepage!');
      setTimeout(() => {
        setSaveSuccessMsg(null);
        setIsSection7ModalOpen(false);
      }, 1500);
    } catch (err) {
      console.error('Error saving Section 7:', err);
      alert('Failed to save Section 7 changes. Please try again.');
    } finally {
      setIsSavingSection7(false);
    }
  };

  const s7Config = siteContent?.homepageSections?.equipment;
  const currentBgColor = s7Config?.backgroundColor || '#F8F9FA';
  const currentAccentColor = s7Config?.accentColor || '#B45309';

  return (
    <ProtectedRoute requiredPermission="dashboard.view">
      <AdminLayout>
        <div className="space-y-8 font-sans">
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-zinc-900 via-zinc-900 to-amber-950/40 border border-zinc-800/90 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden shadow-2xl">
            <div className="space-y-2 relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-[11px] uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 fill-amber-400" />
                STARGAZE CMS CONTROL CENTER
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white uppercase font-mono tracking-tight">
                WELCOME BACK, {profile?.displayName?.toUpperCase() || 'ADMINISTRATOR'}
              </h1>
              <p className="text-xs text-zinc-400 max-w-xl leading-relaxed">
                Logistics, film slate, rental equipment availability, and user management center. Authorized role: <span className="text-amber-400 font-mono font-bold">{role}</span>.
              </p>
            </div>

            <div className="shrink-0 flex flex-wrap gap-2.5 relative z-10 font-mono text-xs">
              <button
                onClick={openSection7Customizer}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold rounded-xl transition shadow-lg shadow-amber-500/10 flex items-center gap-2"
              >
                <Sliders className="w-4 h-4" />
                <span>CUSTOMIZE SECTION 7</span>
              </button>

              <a
                href="/admin/content/home"
                className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl border border-zinc-700 transition flex items-center gap-2"
              >
                <Layers className="w-4 h-4 text-amber-400" />
                <span>HOMEPAGE SECTIONS</span>
              </a>

              {role === 'SUPER_ADMIN' && (
                <a
                  href="/admin/users"
                  className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl border border-zinc-700 transition"
                >
                  Manage Users
                </a>
              )}
            </div>
          </div>

          {/* ============================================================== */}
          {/* FEATURED: SECTION 07 (EQUIPMENT SHOWCASE) CUSTOMIZER & INVENTORY */}
          {/* ============================================================== */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-amber-500/30 hover:border-amber-500/50 rounded-2xl p-6 sm:p-7 shadow-xl space-y-6 transition">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-zinc-800/80 pb-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0">
                  <Camera className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="px-2.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono text-[10px] font-bold uppercase tracking-wider">
                      HOMEPAGE SECTION 07 • HARDWARE & OPTICS
                    </span>
                    <span className={`px-2 py-0.5 rounded font-mono text-[10px] font-bold ${
                      s7Config?.enabled !== false 
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                    }`}>
                      {s7Config?.enabled !== false ? '● ACTIVE ON HOMEPAGE' : '○ HIDDEN'}
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-white uppercase font-serif-cinematic tracking-wide">
                    {s7Config?.heading || 'CAMERA & DOP RENTAL DIVISION'}
                  </h2>
                  <p className="text-xs text-zinc-400 font-sans mt-1 max-w-2xl">
                    {s7Config?.description || 'Customizable hardware showcase with Hollywood-grade cinema packages, daily rates, and live availability.'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={openSection7Customizer}
                  className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-mono text-xs font-bold uppercase tracking-wider transition flex items-center gap-2 shadow-lg shadow-amber-500/20"
                >
                  <Sliders className="w-4 h-4" />
                  <span>Customize Section Styles</span>
                </button>
                <a
                  href="/admin/equipment"
                  className="px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-mono text-xs font-bold uppercase tracking-wider transition border border-zinc-700 flex items-center gap-2"
                >
                  <Camera className="w-4 h-4 text-amber-400" />
                  <span>Manage Gear ({equipmentList.length})</span>
                </a>
                <a
                  href="/"
                  target="_blank"
                  rel="noreferrer"
                  className="p-2.5 rounded-xl bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white transition border border-zinc-700"
                  title="View on Homepage"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Quick Live Preview Snippet of Section 7 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800/80 space-y-1">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block">Eyebrow Label</span>
                <span className="text-xs font-bold font-mono" style={{ color: currentAccentColor }}>
                  {s7Config?.eyebrow || '07 // HARDWARE & OPTICS'}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800/80 space-y-1">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block">Background Theme</span>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full border border-zinc-600 shrink-0" 
                    style={{ backgroundColor: currentBgColor }} 
                  />
                  <span className="text-xs font-mono text-zinc-200">
                    {currentBgColor === '#F8F9FA' ? 'Cinema Light (#F8F9FA)' : currentBgColor}
                  </span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800/80 space-y-1">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block">CTA Action</span>
                <span className="text-xs font-mono text-zinc-300">
                  {s7Config?.ctaLabel || 'BROWSE CATALOGUE'} → {s7Config?.ctaDestination || '/equipment'}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800/80 space-y-1">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block">Active Gear Assets</span>
                <span className="text-xs font-mono text-amber-400 font-bold">
                  {equipmentList.length} Units ({equipmentList.filter(e => e.availability === 'AVAILABLE').length} Available)
                </span>
              </div>
            </div>
          </div>

          {/* ROLE-SPECIFIC METRIC STAT CARDS */}
          {/* 1. SUPER ADMIN METRICS */}
          {role === 'SUPER_ADMIN' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <span className="text-xs font-mono text-zinc-400 uppercase block mb-1">Film Projects</span>
                  <span className="text-3xl font-black text-white font-mono">{projectList.length}</span>
                  <span className="text-[10px] text-amber-400 font-mono block mt-1">
                    {projectList.filter(p => p.status === 'PUBLISHED').length} Published
                  </span>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-400 border border-amber-500/30">
                  <Film className="w-6 h-6" />
                </div>
              </div>

              <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex items-center justify-between group hover:border-amber-500/40 transition">
                <div>
                  <span className="text-xs font-mono text-zinc-400 uppercase block mb-1">Equipment Assets (Sec 07)</span>
                  <span className="text-3xl font-black text-white font-mono">{equipmentList.length}</span>
                  <button 
                    onClick={openSection7Customizer}
                    className="text-[10px] text-amber-400 font-mono block mt-1 hover:underline text-left"
                  >
                    ⚙ Customize Section 7 →
                  </button>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-400 border border-amber-500/30">
                  <Camera className="w-6 h-6" />
                </div>
              </div>

              <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <span className="text-xs font-mono text-zinc-400 uppercase block mb-1">Distribution Rights (Sec 08)</span>
                  <span className="text-3xl font-black text-white font-mono">{distributionList.length}</span>
                  <a href="/admin/distribution" className="text-[10px] text-amber-400 font-mono block mt-1 hover:underline">
                    Manage Rights Catalog →
                  </a>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-400 border border-amber-500/30">
                  <Globe className="w-6 h-6" />
                </div>
              </div>

              <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <span className="text-xs font-mono text-zinc-400 uppercase block mb-1">Total System Users</span>
                  <span className="text-3xl font-black text-white font-mono">1</span>
                  <span className="text-[10px] text-emerald-400 font-mono block mt-1">✓ Active Super Admin</span>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-400 border border-amber-500/30">
                  <Users className="w-6 h-6" />
                </div>
              </div>
            </div>
          )}

          {/* 2. EQUIPMENT MANAGER DASHBOARD */}
          {role === 'EQUIPMENT_MANAGER' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-2">
                <Camera className="w-8 h-8 text-amber-400 mb-2" />
                <h3 className="text-lg font-bold text-white uppercase font-mono">RENTAL INVENTORY</h3>
                <p className="text-3xl font-black text-white font-mono">{equipmentList.length} Packages</p>
                <p className="text-xs text-zinc-400">ARRI ALEXA 35, Cooke Anamorphics, RED V-Raptor XL</p>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                <h3 className="text-lg font-bold text-white uppercase font-mono">AVAILABILITY RATE</h3>
                <p className="text-3xl font-black text-emerald-400 font-mono">
                  {equipmentList.filter(e => e.availability === 'AVAILABLE').length} Available
                </p>
                <p className="text-xs text-zinc-400">Packages ready for immediate soundstage checkout</p>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-2">
                <Sliders className="w-8 h-8 text-amber-400 mb-2" />
                <h3 className="text-lg font-bold text-white uppercase font-mono">SECTION 7 CUSTOMIZER</h3>
                <button
                  onClick={openSection7Customizer}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black text-xs font-mono font-bold rounded-xl mt-2"
                >
                  Edit Showcase Styles →
                </button>
              </div>
            </div>
          )}

          {/* Visual Page Builder & Customizer Shortcuts Banner */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="/admin/pages"
              className="p-5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 transition-all group flex flex-col justify-between shadow-lg"
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Film className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-white uppercase font-display mb-1 group-hover:text-amber-400 transition">
                  Visual Page Builder
                </h3>
                <p className="text-xs text-zinc-400 font-light leading-relaxed">
                  Design, preview, and build custom cinema pages using modular Elementor blocks.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-mono text-amber-400 font-bold">
                <span>MANAGE PAGES</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <a
              href="/admin/customizer"
              className="p-5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 transition-all group flex flex-col justify-between shadow-lg"
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-white uppercase font-display mb-1 group-hover:text-amber-400 transition">
                  Live Theme Customizer
                </h3>
                <p className="text-xs text-zinc-400 font-light leading-relaxed">
                  Customize header layout, logo scale, Section 7 styles, footer columns, and custom CSS.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-mono text-amber-400 font-bold">
                <span>OPEN CUSTOMIZER</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <a
              href="/admin/content/home"
              className="p-5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 transition-all group flex flex-col justify-between shadow-lg"
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Layers className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-white uppercase font-display mb-1 group-hover:text-amber-400 transition">
                  Homepage CMS Modules
                </h3>
                <p className="text-xs text-zinc-400 font-light leading-relaxed">
                  Reorder, configure headings, and customize Section 7 Equipment & Section 8 Distribution.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-mono text-amber-400 font-bold">
                <span>MANAGE ALL SECTIONS</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>
          </div>

          {/* Quick Action Cards & Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                <h3 className="text-sm font-bold text-white uppercase font-mono tracking-wider flex items-center gap-2">
                  <Film className="w-4 h-4 text-amber-500" /> ACTIVE FILM SLATE SUMMARY
                </h3>
                <a href="/admin/projects" className="text-xs font-mono text-amber-400 hover:underline">
                  View All →
                </a>
              </div>

              <div className="space-y-3">
                {projectList.slice(0, 3).map((proj) => (
                  <div
                    key={proj.id}
                    className="p-4 rounded-xl bg-zinc-950 border border-zinc-800/80 flex items-center justify-between text-xs"
                  >
                    <div className="space-y-1">
                      <span className="font-bold text-white block">{proj.title}</span>
                      <span className="text-[10px] text-zinc-500 font-mono">
                        Director: {proj.director} • {proj.genre}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                        proj.status === 'PUBLISHED'
                          ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/50'
                          : 'bg-zinc-900 text-zinc-400 border border-zinc-800'
                      }`}
                    >
                      {proj.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* System Status / Quick Log */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase font-mono tracking-wider flex items-center gap-2 border-b border-zinc-800 pb-4">
                <Clock className="w-4 h-4 text-amber-500" /> SYSTEM STATUS
              </h3>
              <div className="space-y-3 text-xs">
                <div className="p-3 bg-zinc-950 rounded-xl border border-zinc-800/80">
                  <span className="text-[10px] text-amber-400 font-mono uppercase block">Section 07 Status</span>
                  <span className="text-zinc-200">
                    {s7Config?.enabled !== false ? 'Live & Active on Homepage' : 'Hidden'}
                  </span>
                </div>
                <div className="p-3 bg-zinc-950 rounded-xl border border-zinc-800/80">
                  <span className="text-[10px] text-emerald-400 font-mono uppercase block">Firestore Database</span>
                  <span className="text-zinc-200">Connected with Real-Time CMS</span>
                </div>
                <div className="p-3 bg-zinc-950 rounded-xl border border-zinc-800/80">
                  <span className="text-[10px] text-amber-400 font-mono uppercase block">User Session</span>
                  <span className="text-zinc-200">{profile?.email || 'admin@stargaze.com'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* MODAL: SECTION 07 (EQUIPMENT SHOWCASE) QUICK CUSTOMIZER */}
        {/* ============================================================== */}
        {isSection7ModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center">
                    <Sliders className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white font-mono uppercase">
                      CUSTOMIZE SECTION 07: HARDWARE & OPTICS
                    </h3>
                    <p className="text-xs text-zinc-400">
                      Update titles, lead copy, style colors, and CTA buttons directly on the live homepage.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsSection7ModalOpen(false)}
                  className="p-2 text-zinc-400 hover:text-white rounded-xl bg-zinc-800/50 hover:bg-zinc-800 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {saveSuccessMsg && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{saveSuccessMsg}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSaveSection7} className="space-y-5">
                {/* Visibility Toggle */}
                <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-950 border border-zinc-800">
                  <div>
                    <span className="text-xs font-mono font-bold text-white uppercase block">Show on Homepage</span>
                    <span className="text-[11px] text-zinc-400">When disabled, this section is hidden from public visitors.</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={s7Enabled}
                    onChange={(e) => setS7Enabled(e.target.checked)}
                    className="w-5 h-5 rounded accent-amber-500 cursor-pointer"
                  />
                </div>

                {/* Eyebrow and Heading */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-zinc-400 uppercase mb-1">Eyebrow Tagline</label>
                    <input
                      type="text"
                      value={s7Eyebrow}
                      onChange={(e) => setS7Eyebrow(e.target.value)}
                      placeholder="e.g. 07 // HARDWARE & OPTICS"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-mono text-xs focus:border-amber-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-zinc-400 uppercase mb-1">Main Heading</label>
                    <input
                      type="text"
                      value={s7Heading}
                      onChange={(e) => setS7Heading(e.target.value)}
                      placeholder="e.g. CAMERA & DOP RENTAL DIVISION"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-mono text-xs focus:border-amber-500 outline-none"
                    />
                  </div>
                </div>

                {/* Lead Description */}
                <div>
                  <label className="block text-xs font-mono text-zinc-400 uppercase mb-1">Section Description / Subtitle</label>
                  <textarea
                    rows={2}
                    value={s7Description}
                    onChange={(e) => setS7Description(e.target.value)}
                    placeholder="Provide overview of equipment packages, rental policies, and soundstage gear..."
                    className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-sans text-xs focus:border-amber-500 outline-none"
                  />
                </div>

                {/* CTA Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-zinc-400 uppercase mb-1">Call-to-Action Text</label>
                    <input
                      type="text"
                      value={s7CtaLabel}
                      onChange={(e) => setS7CtaLabel(e.target.value)}
                      placeholder="BROWSE CATALOGUE"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-mono text-xs focus:border-amber-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-zinc-400 uppercase mb-1">CTA Destination Link</label>
                    <input
                      type="text"
                      value={s7CtaDestination}
                      onChange={(e) => setS7CtaDestination(e.target.value)}
                      placeholder="/equipment"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white font-mono text-xs focus:border-amber-500 outline-none"
                    />
                  </div>
                </div>

                {/* Background Styling Presets */}
                <div className="p-4 rounded-2xl bg-zinc-950 border border-zinc-800 space-y-3">
                  <label className="block text-xs font-mono text-zinc-400 uppercase">
                    Section Background Color Style
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { id: '#F8F9FA', label: 'Cinema Light', textDark: true },
                      { id: '#FFFFFF', label: 'Clean White', textDark: true },
                      { id: '#0A0A0C', label: 'Midnight Slate', textDark: false },
                      { id: '#000000', label: 'Pitch Black', textDark: false },
                    ].map((preset) => (
                      <button
                        type="button"
                        key={preset.id}
                        onClick={() => setS7BgColor(preset.id)}
                        className={`p-3 rounded-xl border flex flex-col items-center gap-1.5 transition ${
                          s7BgColor.toUpperCase() === preset.id.toUpperCase()
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
                        }`}
                      >
                        <div
                          className="w-6 h-6 rounded-full border border-zinc-700 shadow-xs"
                          style={{ backgroundColor: preset.id }}
                        />
                        <span className="text-[10px] font-mono font-bold text-zinc-300">{preset.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-[11px] font-mono text-zinc-500 uppercase">Custom Hex:</span>
                    <input
                      type="text"
                      value={s7BgColor}
                      onChange={(e) => setS7BgColor(e.target.value)}
                      className="px-3 py-1.5 rounded-lg bg-zinc-900 border border-zinc-800 text-white font-mono text-xs w-32 outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Accent Color Presets */}
                <div className="p-4 rounded-2xl bg-zinc-950 border border-zinc-800 space-y-3">
                  <label className="block text-xs font-mono text-zinc-400 uppercase">
                    Eyebrow & Details Accent Color
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {[
                      { hex: '#B45309', label: 'Amber Classic' },
                      { hex: '#D97706', label: 'Golden Flare' },
                      { hex: '#DC2626', label: 'Crimson' },
                      { hex: '#059669', label: 'Emerald' },
                      { hex: '#2563EB', label: 'Sapphire' },
                    ].map((pal) => (
                      <button
                        type="button"
                        key={pal.hex}
                        onClick={() => setS7AccentColor(pal.hex)}
                        className={`p-2.5 rounded-xl border flex flex-col items-center gap-1 transition ${
                          s7AccentColor.toUpperCase() === pal.hex.toUpperCase()
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
                        }`}
                      >
                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: pal.hex }} />
                        <span className="text-[10px] font-mono text-zinc-400">{pal.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live Section Header Mini-Preview */}
                <div className="p-4 rounded-2xl border space-y-2 transition" style={{ backgroundColor: s7BgColor }}>
                  <div className="flex items-center justify-between border-b pb-2" style={{ borderColor: s7BgColor.startsWith('#0') ? '#27272a' : '#e4e4e7' }}>
                    <div>
                      <span className="text-[10px] font-mono font-bold tracking-widest block" style={{ color: s7AccentColor }}>
                        {s7Eyebrow || '07 // HARDWARE & OPTICS'}
                      </span>
                      <h4 className={`text-base font-bold font-serif-cinematic uppercase ${s7BgColor.startsWith('#0') ? 'text-white' : 'text-zinc-900'}`}>
                        {s7Heading || 'CAMERA & DOP RENTAL DIVISION'}
                      </h4>
                      {s7Description && (
                        <p className={`text-[11px] max-w-md ${s7BgColor.startsWith('#0') ? 'text-zinc-400' : 'text-zinc-600'}`}>
                          {s7Description}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] font-mono font-bold" style={{ color: s7AccentColor }}>
                      {s7CtaLabel} →
                    </span>
                  </div>
                  <span className="text-[9px] font-mono text-zinc-500 block text-right">
                    Interactive Header Preview
                  </span>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
                  <button
                    type="button"
                    onClick={() => setIsSection7ModalOpen(false)}
                    className="px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono text-xs uppercase"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingSection7}
                    className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-mono text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-amber-500/20 disabled:opacity-50"
                  >
                    {isSavingSection7 ? <Sparkles className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Save & Publish Live</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AdminLayout>
    </ProtectedRoute>
  );
};
